// This file is generated by [oats][0] and should not be edited by hand.
//
// [0]: https://github.com/influxdata/oats

export interface Scripts {
  scripts?: Script[]
}

export interface Script {
  readonly id?: string
  name: string
  description?: string
  orgID: string
  script: string
  language?: ScriptLanguage
  url?: string
  readonly createdAt?: string
  readonly updatedAt?: string
}

export type ScriptLanguage = 'flux'

export interface Error {
  readonly code:
    | 'internal error'
    | 'not found'
    | 'conflict'
    | 'invalid'
    | 'unprocessable entity'
    | 'empty value'
    | 'unavailable'
    | 'forbidden'
    | 'too many requests'
    | 'unauthorized'
    | 'method not allowed'
    | 'request too large'
    | 'unsupported media type'
  readonly message?: string
  readonly op?: string
  readonly err?: string
}

export interface ScriptCreateRequest {
  name: string
  description: string
  script: string
  language: ScriptLanguage
}

export interface ScriptUpdateRequest {
  name?: string
  description?: string
  script?: string
}

export interface ScriptInvocationParams {
  params?: any
}

export type ScriptHTTPResponseData = string

interface RequestOptions {
  signal?: AbortSignal
}

export type RequestHandler = (
  url: string,
  query: string,
  init: RequestInit
) => {url: string; query: string; init: RequestInit}
export type ResponseHandler = (
  status: number,
  headers: Headers,
  data: any
) => {status: number; headers: Headers; data: any}

const RequestContext = function(
  requestHandler: RequestHandler,
  responseHandler: ResponseHandler
) {
  this.requestHandler = requestHandler
  this.responseHandler = responseHandler
}

RequestContext.prototype.request = async function(
  method: string,
  url: string,
  params: any = {},
  options: RequestOptions = {}
): Promise<any> {
  const requestHeaders = new Headers(params.headers)
  const contentType = requestHeaders.get('Content-Type') || ''

  if (params.auth) {
    const credentials = btoa(`${params.auth.username}:${params.auth.password}`)

    requestHeaders.append('Authorization', `Basic ${credentials}`)
  }

  const body =
    params.data && contentType.includes('json')
      ? JSON.stringify(params.data)
      : params.data

  const query = params.query ? `?${new URLSearchParams(params.query)}` : ''

  const {
    url: middlewareUrl,
    query: middlewareQuery,
    init,
  } = this.requestHandler(url, query, {
    method,
    body,
    credentials: 'same-origin',
    signal: options.signal,
    headers: requestHeaders,
  })

  const response = await fetch(`${middlewareUrl}${middlewareQuery}`, init)

  const {status, headers} = response
  const responseContentType = headers.get('Content-Type') || ''

  let data

  if (responseContentType.includes('json')) {
    data = await response.json()
  } else if (responseContentType.includes('octet-stream')) {
    data = await response.blob()
  } else {
    data = await response.text()
  }

  return this.responseHandler(status, headers, data)
}

RequestContext.prototype.setRequestHandler = function(
  requestHandler: RequestHandler
) {
  this.requestHandler = requestHandler
}

RequestContext.prototype.setResponseHandler = function(
  responseHandler: ResponseHandler
) {
  this.responseHandler = responseHandler
}

const rc = new RequestContext(
  (url, query, init) => {
    return {url, query, init}
  },
  (status, headers, data) => {
    return {status, headers, data}
  }
)
const request = rc.request.bind(rc)
const setRequestHandler = rc.setRequestHandler.bind(rc)
const setResponseHandler = rc.setResponseHandler.bind(rc)

export {request, setRequestHandler, setResponseHandler}

export interface GetScriptsParams {
  query?: {
    limit?: number
    offset?: number
  }
}

type GetScriptsResult =
  | GetScriptsOKResult
  | GetScriptsBadRequestResult
  | GetScriptsUnauthorizedResult
  | GetScriptsInternalServerErrorResult
  | GetScriptsDefaultResult

interface GetScriptsOKResult {
  status: 200
  headers: Headers
  data: Scripts
}

interface GetScriptsBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface GetScriptsUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface GetScriptsInternalServerErrorResult {
  status: 500
  headers: Headers
  data: Error
}

interface GetScriptsDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getScripts = (
  params: GetScriptsParams,
  options: RequestOptions = {}
): Promise<GetScriptsResult> =>
  request('GET', '/api/v2/scripts', params, options) as Promise<
    GetScriptsResult
  >

export interface PostScriptParams {
  data: ScriptCreateRequest
}

type PostScriptResult =
  | PostScriptCreatedResult
  | PostScriptBadRequestResult
  | PostScriptUnauthorizedResult
  | PostScriptUnprocessableEntityResult
  | PostScriptInternalServerErrorResult
  | PostScriptDefaultResult

interface PostScriptCreatedResult {
  status: 201
  headers: Headers
  data: Script
}

interface PostScriptBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface PostScriptUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PostScriptUnprocessableEntityResult {
  status: 422
  headers: Headers
  data: Error
}

interface PostScriptInternalServerErrorResult {
  status: 500
  headers: Headers
  data: Error
}

interface PostScriptDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postScript = (
  params: PostScriptParams,
  options: RequestOptions = {}
): Promise<PostScriptResult> =>
  request(
    'POST',
    '/api/v2/scripts',
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PostScriptResult>

export interface GetScriptParams {
  scriptID: string
}

type GetScriptResult = GetScriptOKResult | GetScriptDefaultResult

interface GetScriptOKResult {
  status: 200
  headers: Headers
  data: Script
}

interface GetScriptDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const getScript = (
  params: GetScriptParams,
  options: RequestOptions = {}
): Promise<GetScriptResult> =>
  request(
    'GET',
    `/api/v2/scripts/${params.scriptID}`,
    params,
    options
  ) as Promise<GetScriptResult>

export interface PatchScriptParams {
  scriptID: string

  data: ScriptUpdateRequest
}

type PatchScriptResult = PatchScriptOKResult | PatchScriptDefaultResult

interface PatchScriptOKResult {
  status: 200
  headers: Headers
  data: Script
}

interface PatchScriptDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const patchScript = (
  params: PatchScriptParams,
  options: RequestOptions = {}
): Promise<PatchScriptResult> =>
  request(
    'PATCH',
    `/api/v2/scripts/${params.scriptID}`,
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PatchScriptResult>

export interface DeleteScriptParams {
  scriptID: string
}

type DeleteScriptResult =
  | DeleteScriptNoContentResult
  | DeleteScriptDefaultResult

interface DeleteScriptNoContentResult {
  status: 204
  headers: Headers
  data: any
}

interface DeleteScriptDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const deleteScript = (
  params: DeleteScriptParams,
  options: RequestOptions = {}
): Promise<DeleteScriptResult> =>
  request(
    'DELETE',
    `/api/v2/scripts/${params.scriptID}`,
    params,
    options
  ) as Promise<DeleteScriptResult>

export interface PostScriptsInvokeParams {
  scriptID: string

  data?: ScriptInvocationParams
}

type PostScriptsInvokeResult =
  | PostScriptsInvokeOKResult
  | PostScriptsInvokeBadRequestResult
  | PostScriptsInvokeUnauthorizedResult
  | PostScriptsInvokeNotFoundResult
  | PostScriptsInvokeInternalServerErrorResult
  | PostScriptsInvokeDefaultResult

interface PostScriptsInvokeOKResult {
  status: 200
  headers: Headers
  data: ScriptHTTPResponseData
}

interface PostScriptsInvokeBadRequestResult {
  status: 400
  headers: Headers
  data: Error
}

interface PostScriptsInvokeUnauthorizedResult {
  status: 401
  headers: Headers
  data: Error
}

interface PostScriptsInvokeNotFoundResult {
  status: 404
  headers: Headers
  data: Error
}

interface PostScriptsInvokeInternalServerErrorResult {
  status: 500
  headers: Headers
  data: Error
}

interface PostScriptsInvokeDefaultResult {
  status: 500
  headers: Headers
  data: Error
}

export const postScriptsInvoke = (
  params: PostScriptsInvokeParams,
  options: RequestOptions = {}
): Promise<PostScriptsInvokeResult> =>
  request(
    'POST',
    `/api/v2/scripts/${params.scriptID}/invoke`,
    {...params, headers: {'Content-Type': 'application/json'}},
    options
  ) as Promise<PostScriptsInvokeResult>
